import type React from 'react'
import type { JSX } from 'react'
import type { ILabelInlinePrimaryTemplateProps } from 'atoms/Label/Templates'
import type { IProgressBarAccentTemplate } from 'atoms/ProgressBar/Templates'
import type { ITVShowPopupProps } from 'blocks/Popup/Templates'
import type { ITextBlockDescriptionType1 } from 'blocks/TextBlock/Templates'
import type { ICard } from '../Card'

import { Children } from 'react'
import { LabelFonts } from 'atoms/Label'
import { LabelInlinePrimaryTemplate } from 'atoms/Label/Templates'
import { OverlayLayoutColors } from 'atoms/OverlayLayout'
import { PartnerProgramCoverDesktop } from 'atoms/PartnerProgramCover'
import { ProgressBarAccentTemplate } from 'atoms/ProgressBar/Templates'
import { TextBox, TextBoxColors, TextBoxTypes } from 'atoms/TextBox'
import { TextBlockHorizontalType1 } from 'blocks/TextBlock/Templates'
import { useBooleanState } from 'utils/hooks'
import { COLOR } from 'vars/index'
import { ImageBackgroundSC } from '../base-card'
import { Card } from '../Card'
import { CardCorners } from '../Card.h'
import {
  CardChild,
  CardContent,
  CardStyled,
  CardStyledContent,
  ContentLabelSC,
  DETAILS_SYNOPSIS_MAX_LINES,
  DetailsSynopsisSC,
  DetailsTextSC,
  DetailsTitleSC,
  InlinePlaySolidButtonSC,
  PrimaryInfoButtonSC,
} from './TVShowEpisodeCard.styled'

export interface ITVShowEpisodeCardProps extends Omit<
  ICard,
  | 'children'
  | 'gridCard'
  | 'backgroundColorForLogo'
  | 'viewedButtonFavorite'
  | 'selected'
  | 'borderVisible'
  | 'directorsPopup'
  | 'starsPopup'
> {
  progress?: IProgressBarAccentTemplate['progress']
  labelTitle?: ILabelInlinePrimaryTemplateProps['title']
  title?: ITextBlockDescriptionType1['title']
  description?: ITextBlockDescriptionType1['description']
  children?: JSX.Element[]
  viewedButtonWithHover?: boolean
  titlePopup?: ITVShowPopupProps['title']
  descriptionPopup?: ITVShowPopupProps['description']
  labelsPopup?: ITVShowPopupProps['labels']
  listPopup?: ITVShowPopupProps['list']
  viewedButtonInfo?: boolean
  textDescriptionPopup?: string
  directorsPopup?: string
  starsPopup?: string
  onInfoButtonClick?: () => void
  onInfoPopupClose?: () => void
  isPopupOpen?: boolean
  episodeIdInInfoPopup?: null | number
  id?: any
  dataTestId?: string
  /**
   * FP-4261: render the card as the episode card of the series details row
   * (Figma `16273:212314`) — the episode number, the title and the synopsis
   * stacked under the poster, and the running time in the concept's caption
   * font. Opt-in, so the rows that keep the old two-line card are untouched.
   */
  detailsCard?: boolean
  /** Episode synopsis, the third line the details card adds under the title. */
  episodeDescription?: ITextBlockDescriptionType1['description']
}

export const TVShowEpisodeCard = ({
  children,
  posterUrl = '',
  width = '100%',
  height = '100%',
  labelTitle = '',
  title = '',
  description = '',
  progress = 0,
  viewedButtonWithHover = true,
  viewedButtonInfo = false,

  textDescriptionPopup: _unused1,
  directorsPopup: _unused2,
  starsPopup: _unused3,
  onInfoButtonClick,
  onInfoPopupClose: _unused4,
  isPopupOpen: _unused5,
  id: _unused6,
  episodeIdInInfoPopup: _unused7,
  overlayHeight = '100%',
  overlayVerticalColor,
  overlayGradient = OverlayLayoutColors.EPISODE_CARD_VERTICAL_OVERLAY,
  corners = CardCorners.CORNERS_0,
  overlayOnHoverEnabled: _unused8 = true,
  backgroundColorHovered: _unused9,
  purchaseTitle,
  detailsCard = false,
  episodeDescription = '',
  ...rest
}: ITVShowEpisodeCardProps): JSX.Element => {
  const [isPosterLoaded, handlePosterLoading, handlePosterError] =
    useBooleanState(true)

  const handleInfoBtnClick = (e?: React.MouseEvent<Element, MouseEvent>) => {
    e?.stopPropagation()
    if (onInfoButtonClick) onInfoButtonClick()
  }

  const posterContent = (
    <ContentLabelSC>
      <LabelInlinePrimaryTemplate
        title={labelTitle}
        font={detailsCard ? LabelFonts.FontXs : undefined}
        color={detailsCard ? COLOR.TEXT_ICON_PRIMARY : undefined}
      />

      {progress ? <ProgressBarAccentTemplate progress={progress} /> : null}
    </ContentLabelSC>
  )

  const detailsText = (
    <DetailsTextSC>
      {title && (
        <TextBox
          title={title}
          width='100'
          font={TextBoxTypes.FontXs}
          color={TextBoxColors.TEXT_ICON_SECONDARY}
          maxLines={1}
        />
      )}
      {description && (
        <DetailsTitleSC>
          <TextBox
            title={description}
            width='100'
            font={TextBoxTypes.FontSmBold}
            color={TextBoxColors.TEXT_ICON_PRIMARY}
            maxLines={1}
          />
        </DetailsTitleSC>
      )}
      {episodeDescription && (
        <DetailsSynopsisSC>
          <TextBox
            title={episodeDescription}
            width='100'
            font={TextBoxTypes.FontXs}
            color={TextBoxColors.TEXT_ICON_SECONDARY}
            maxLines={DETAILS_SYNOPSIS_MAX_LINES}
          />
        </DetailsSynopsisSC>
      )}
    </DetailsTextSC>
  )

  const poster = isPosterLoaded ? posterUrl : ''
  const emptyPoster = !poster || !isPosterLoaded
  const childrenList = Children.toArray(children)
  const actionsSlot = childrenList[0]
  const contentSlot = childrenList[1]

  const BackgroundCover = emptyPoster ? (
    <ImageBackgroundSC>
      <PartnerProgramCoverDesktop />
    </ImageBackgroundSC>
  ) : (
    <></>
  )

  const RenderChild = () => {
    return (
      <CardChild>
        <CardContent>
          {actionsSlot ??
            (viewedButtonInfo ? (
              <PrimaryInfoButtonSC onClick={handleInfoBtnClick} />
            ) : null)}
          {viewedButtonWithHover && <InlinePlaySolidButtonSC />}
        </CardContent>
      </CardChild>
    )
  }

  return (
    <CardStyled width={width} height={height}>
      <CardStyledContent
        viewedButtonInfo={viewedButtonInfo}
        viewedButtonWithHover={viewedButtonWithHover}
        detailsCard={detailsCard}
      >
        <Card
          posterUrl={poster}
          width={width}
          hidePriceOnHover={false}
          overlayEnabled={true}
          overlayHeight={overlayHeight}
          overlayVerticalColor={overlayVerticalColor}
          overlayGradient={overlayGradient}
          corners={corners}
          handlePosterLoading={handlePosterLoading}
          handlePosterError={handlePosterError}
          posterContent={posterContent}
          purchaseTitle={purchaseTitle}
          backgroundCover={BackgroundCover}
          // the details row sizes the poster from the carousel card width
          gridCard={detailsCard}
          {...rest}
        >
          <RenderChild />
        </Card>
        {contentSlot ??
          (detailsCard ? (
            detailsText
          ) : (
            <TextBlockHorizontalType1 title={title} description={description} />
          ))}
      </CardStyledContent>
    </CardStyled>
  )
}

TVShowEpisodeCard.displayName = 'TVShowEpisodeCard'
